import type { RouteCompletionDraft } from '../types';
import type { RouteViewModelRoutePenalty } from '../../../types/routeViewModel';
import type { RouteExpense } from '../../../store/api/financeApi';

export type OperationalCompletionSnapshotPayload = {
  completedAt: string;
  completedBy: number;
  logistComment: string;
  penaltyIds: number[];
  expenseIds: number[];
  financeSummary: {
    invoiceReady: boolean;
    updReady: boolean;
    originalsReady: boolean;
  };
  warnings: string[];
  unresolvedIncidentIds: number[];
};

export type CompletionSnapshot = {
  completedAt: string;
  completedBy: number;
  logistComment: string;
  penalties: Array<{ id: number; name: string; amount: number | null; comment: string | null }>;
  expenses: Array<{ id: number; type: string; amount: number; status: string }>;
  financeSummary: OperationalCompletionSnapshotPayload['financeSummary'];
  warnings: string[];
  unresolvedIncidentIds: number[];
};

export function buildCompletionSnapshotFromFinalize(
  payload: OperationalCompletionSnapshotPayload,
  penalties: RouteViewModelRoutePenalty[],
  expenses: RouteExpense[]
): CompletionSnapshot {
  const penaltySet = new Set(payload.penaltyIds);
  const expenseSet = new Set(payload.expenseIds);
  return {
    completedAt: payload.completedAt,
    completedBy: payload.completedBy,
    logistComment: payload.logistComment,
    penalties: penalties
      .filter((p) => penaltySet.has(p.id))
      .map((p) => ({
        id: p.id,
        name: p.agreementPenalty?.name ?? 'Штраф',
        amount: p.calculatedAmount,
        comment: p.comment
      })),
    expenses: expenses
      .filter((e) => expenseSet.has(e.id))
      .map((e) => ({
        id: e.id,
        type: e.type ?? 'OTHER',
        amount: Number(e.amountWithVAT) || 0,
        status: e.status ?? 'DRAFT'
      })),
    financeSummary: payload.financeSummary,
    warnings: payload.warnings,
    unresolvedIncidentIds: payload.unresolvedIncidentIds
  };
}

export function buildSnapshotPayloadFromDraft(params: {
  draft: RouteCompletionDraft;
  userId: number;
  savedPenaltyIds: number[];
  savedExpenseIds: number[];
  warnings: string[];
  unresolvedIncidentIds: number[];
  financeSummary: OperationalCompletionSnapshotPayload['financeSummary'];
}): OperationalCompletionSnapshotPayload {
  return {
    completedAt: new Date().toISOString(),
    completedBy: params.userId,
    logistComment: params.draft.logistComment.trim(),
    penaltyIds: params.savedPenaltyIds,
    expenseIds: params.savedExpenseIds,
    financeSummary: params.financeSummary,
    warnings: params.warnings,
    unresolvedIncidentIds: params.unresolvedIncidentIds
  };
}
